import React from 'react';
import { FloatingAdinkra } from '../components/Decorators';
import { RevealOnScroll } from '../hooks/useScrollReveal';
import portrait from '../assets/images/adwoa-potrait.png';
import sankofaImg from '../assets/images/sankofa.jpg';
import gyeNyameImg from '../assets/images/gye-nyame.jpg';
import dwennimmenImg from '../assets/images/dwenimen.jpg';

const AdinkraImg = ({ src, alt }) => (
  <img src={src} alt={alt} className="story-adinkra-img" />
);

const StoryPage = () => (
  <div className="adinkra-texture-bg" style={{ padding: '5rem 2rem', position: 'relative' }}>
    <FloatingAdinkra count={5} color="rgba(107, 85, 32, 0.06)" />
    <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 2 }}>

      <RevealOnScroll>
        <div className="section-header">
          <h2 className="section-title">My Story</h2>
          <p className="section-subtitle">
            From Curiosity to Creation — a Ghanaian journey in grit, faith, and design.
          </p>
        </div>
        <div className="kente-border"></div>
      </RevealOnScroll>

      <RevealOnScroll direction="scale" delay={150}>
        <div style={{ display: 'flex', justifyContent: 'center', margin: '2.5rem 0 1rem' }}>
          <div className="story-portrait-frame">
            <img src={portrait} alt="Adwoa B. Acheampong" className="story-portrait" />
          </div>
        </div>
      </RevealOnScroll>

      <div className="story-narrative">
        <RevealOnScroll>
          <p className="lead">
            I was the child who took things apart to see how they worked — and then rebuilt them into something better. That child never really left.
          </p>
        </RevealOnScroll>

        {/* Chapter 1 */}
        <RevealOnScroll direction="left">
          <div className="story-chapter">
            <div className="story-chapter-header">
              <AdinkraImg src={sankofaImg} alt="Sankofa" />
              <div>
                <div className="story-chapter-number">Chapter One</div>
                <h3>Roots & Curiosity</h3>
              </div>
            </div>
            <div className="story-chapter-divider"></div>
            <p>
              I grew up in Ghana surrounded by a culture that teaches you to look backward in order to move forward — <em>Sankofa</em>. My earliest memories are of asking "why?" until the adults ran out of patience. Why does the market pulse at certain hours? Why do some traders thrive while others fold? Why do systems that look identical from the outside produce such different outcomes?
            </p>
            <p>
              Those questions became a career. I studied Computer Engineering because I wanted the vocabulary to build answers, not just ask questions. But engineering alone was too narrow — I was always more interested in the messy interface between people, process, and technology. That's the terrain where real businesses get made or broken.
            </p>
          </div>
        </RevealOnScroll>

        {/* Pull quote */}
        <RevealOnScroll direction="scale">
          <blockquote className="story-pullquote">
            "The best systems are not the most complex — they are the ones that make the right thing the easy thing. Everything I build starts from that conviction."
          </blockquote>
        </RevealOnScroll>

        {/* Chapter 2 */}
        <RevealOnScroll direction="right">
          <div className="story-chapter">
            <div className="story-chapter-header">
              <AdinkraImg src={gyeNyameImg} alt="Gye Nyame" />
              <div>
                <div className="story-chapter-number">Chapter Two</div>
                <h3>Resilience & Faith</h3>
              </div>
            </div>
            <div className="story-chapter-divider"></div>
            <p>
              Every founder, operator, and builder I respect has one thing in common: they've been knocked down in ways that would have ended a weaker version of themselves. I am no exception. I've watched projects stall, partnerships fray, and plans that looked perfect on paper collapse on contact with reality.
            </p>
            <p>
              What carried me through was not optimism — optimism is fragile. It was <em>Gye Nyame</em>: the supreme confidence that setbacks are tuition, not verdicts. Faith, for me, is not passive. It's the discipline of showing up the next morning with a clearer plan than the one that failed the night before.
            </p>
            <p>
              I've rewritten standard operating procedures from scratch after an inherited system produced a loss. I've rebuilt broken marketplaces by interviewing every vendor until I understood the real problem, not the one the executive team assumed. Resilience is a muscle, and mine has been well-trained.
            </p>
          </div>
        </RevealOnScroll>

        {/* Chapter 3 */}
        <RevealOnScroll direction="left">
          <div className="story-chapter">
            <div className="story-chapter-header">
              <AdinkraImg src={dwennimmenImg} alt="Dwennimmen" />
              <div>
                <div className="story-chapter-number">Chapter Three</div>
                <h3>Leadership & Creation</h3>
              </div>
            </div>
            <div className="story-chapter-divider"></div>
            <p>
              The <em>Adinkrahene</em> — the "chief of Adinkra symbols" — is three concentric circles radiating outward. It is the symbol of leadership by example, of greatness that draws others into alignment rather than demanding it. That is the kind of leader I try to be, and the kind of organization I try to build.
            </p>
            <p>
              Today I architect business operations for ventures that want to grow without losing their soul. I design command centers, staff platforms, and data dashboards that turn guesswork into decisions. I write the playbooks that new hires can actually follow. I sit with founders and help them see their own businesses with fresh eyes. And on the side, I am building my own — The Merchant Hub, Real Estate Management Hub, Crushed Diamond, Havilah. Ventures that exist because I believe Africa is not a problem to be solved but a platform to be built.
            </p>
            <p>
              I am a networker, a collaborator, and an unrepentant builder. I delegate with empathy because I know what it's like to be the person doing the work. I hire for grit over polish because polish can be taught. And I measure success not by the size of the title but by the quality of the machine left running when I walk away.
            </p>
          </div>
        </RevealOnScroll>

        {/* Closing */}
        <RevealOnScroll direction="scale">
          <div className="story-closing">
            <p style={{ fontStyle: 'italic', fontSize: '1.2rem' }}>
              My journey is about engineering "double domino effects" — passionate, gritty solutions that scale. From optimizing marketplaces to rewriting SOPs, I architect machines where people and logic align for lasting impact.
            </p>
            <p className="story-signature">— Adwoa</p>
            <p style={{ fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--gold-dark)', marginTop: '0.5rem' }}>
              Business Operations Architect · Accra, Ghana
            </p>
          </div>
        </RevealOnScroll>
      </div>
    </div>
  </div>
);

export default StoryPage;
